import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Box,
  Typography,
  Card,
  Stack,
  Button,
  Grid,
  Divider,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from '@mui/material';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import axiosInstance, { getErrorMessage } from 'utils/axios';
import { FormProvider, RHFTextField } from 'components/hook-form';
import { LoadingButton } from '@mui/lab';
import CRUD from 'utils/Crud';
import { fNumber } from 'utils/formatNumber';
import { useTheme } from '@mui/material/styles';

const style = (isMobile) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: isMobile ? '95%' : 600,
  maxHeight: isMobile ? '95vh' : 850,
  minHeight: isMobile ? undefined : 600,
  overflowY: 'auto',
  bgcolor: 'background.paper',
  border: '2px solid #f0f0f0',
  boxShadow: 24,
  p: { xs: 1, sm: 2 },
  borderRadius: 2,
});

const REASONS = [
  { value: '', label: '-- Select Reason --', prefill: '' },
  { value: 'wrong_face_value', label: 'Wrong Face Value', prefill: 'The card was submitted with an incorrect face value.' },
  { value: 'market_price_changed', label: 'Market Price Changed', prefill: 'The market price has changed since the card was submitted.' },
  { value: 'other', label: 'Other Reason', prefill: '' },
];

// Yup schema with conditional remark validation
const NewUserSchema = Yup.object().shape({
  amount: Yup.number()
    .typeError("Card amount must be a number")
    .required("Card amount is required"),
  number_of_cards: Yup.number()
    .typeError("Number of cards must be a number")
    .integer("Number of cards must be an integer")
    .min(1, "At least 1 card is required")
    .required("Number of cards is required"),
  password: Yup.string().required('Password is required'),
  reason: Yup.string().required('Please select a reason'),
  remark: Yup.string().when('reason', {
    is: 'other',
    then: Yup.string().required('Remark is required when "Other Reason" is selected'),
    otherwise: Yup.string(),
  }),
  settlement_amount: Yup.string().required("Settlement amount is required"),
  confirm: Yup.boolean().oneOf([true], "You must confirm the changes"),
});

GiftcardOrderDisputeModal.propTypes = {
  handleClose: PropTypes.func,
  handleComplete: PropTypes.func,
  order: PropTypes.object.isRequired,
};

export default function GiftcardOrderDisputeModal({
  handleClose,
  handleComplete,
  order,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues: {
      amount: fNumber(order.amount),
      password: '',
      reason: '',
      remark: '',
      number_of_cards: order.number_of_cards,
      settlement_amount: fNumber(order.settlement_amount),
      confirm: false,
    },
  });

  const {
    reset,
    watch,
    control,
    setValue,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  const values = watch();

  // Prefill remark when reason changes and is NOT 'other'
  useEffect(() => {
    const selectedReason = REASONS.find(r => r.value === values.reason);
    if (selectedReason && selectedReason.value !== 'other') {
      setValue('remark', selectedReason.prefill, { shouldValidate: true });
    } else if (selectedReason && selectedReason.value === 'other') {
      setValue('remark', '', { shouldValidate: true });
    }
  }, [values.reason, setValue]);

  const onSubmit = async () => {
    try {
      const amt = String(values.amount).replace(/,/g, '');

      values.amount = amt;
      values.order_id = order.order_id;
      values.settlement_amount = String(values.settlement_amount).replace(/,/g, '');
      // const formData = new FormData();
      // formData.append('password', values.password);
      // formData.append('reason', values.reason);
      // formData.append('remark', values.remark);
      // formData.append('amount', amt);
      // formData.append('order_id', order.order_id);
      // formData.append('number_of_cards', values.number_of_cards);
      // formData.append('settlement_amount', String(values.settlement_amount).replace(/,/g, ''));

      const ret = await axiosInstance.post(CRUD.order.giftcardOrderDispute, values);
      reset();
      enqueueSnackbar('Dispute submitted successfully!');
      handleComplete(ret.data);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error));
    }
  };

  // Helper for currency formatting
  function _currencyFormat(e, field) {
    const v = e.currentTarget.value.replaceAll(",", "");
    const _v = parseFloat(v || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    methods.setValue(field, _v);
  }

  return (
    <Modal
      open
      onClose={(e, r) => {
        if (r && r === "backdropClick") return;
        handleClose();
      }}
      aria-labelledby="dispute-modal-title"
      aria-describedby="dispute-modal-description"
      hideBackdrop={false}
      sx={{ zIndex: 1300 }}
    >
      <Box sx={style(isMobile)}>
        <Typography id="dispute-modal-title" variant="h6" component="h2" mb={1}>
          File a Dispute
        </Typography>
        <Typography id="dispute-modal-description" variant="body2" mb={2}>
          Please review the order details and provide the required information to file a dispute.
        </Typography>

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Card sx={{ p: { xs: 1, sm: 3 }, mt: 2 }}>
            {/* Order Details */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {order.category} - {order.type_name} ({order.country})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Order ID: {order.order_id}
              </Typography>
              <Divider sx={{ my: 1 }} />
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>Note:</b> Only use this option if the user entered the wrong card value.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <RHFTextField
                    name="amount"
                    label={`Total Card Amount (${order.currency})`}
                    required
                    sx={{ mb: 2 }}
                    error={!!errors.amount}
                    helperText={errors.amount?.message || "Enter the correct card amount."}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <RHFTextField
                    name="number_of_cards"
                    label="Number of Cards"
                    required
                    sx={{ mb: 2 }}
                    error={!!errors.number_of_cards}
                    helperText={errors.number_of_cards?.message || "Number of valid uploaded cards"}
                  />
                </Grid>
              </Grid>

              <RHFTextField
                name="settlement_amount"
                label="Settlement Amount (NGN)"
                onFocus={(e) => { e.currentTarget.select(); }}
                onBlur={(e) => { _currencyFormat(e, "settlement_amount"); }}
                required
                sx={{ mb: 2 }}
                error={!!errors.settlement_amount}
                helperText={errors.settlement_amount?.message || "Settlement amount in NGN after dispute resolution."}
              />

              {/* Reason select */}
              <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.reason}>
                <InputLabel id="reason-label">Select Reason</InputLabel>
                <Controller
                  name="reason"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="reason-label"
                      label="Select Reason"
                      fullWidth
                    >
                      {REASONS.map((r) => (
                        <MenuItem key={r.value} value={r.value}>
                          {r.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.reason && (
                  <FormHelperText>{errors.reason.message}</FormHelperText>
                )}
              </FormControl>

              {/* Conditionally show remark textarea if "Other Reason" */}
              {values.reason === 'other' && (
                <RHFTextField
                  name="remark"
                  label="Remark"
                  multiline
                  minRows={2}
                  sx={{ mb: 2 }}
                  error={!!errors.remark}
                  helperText={errors.remark?.message || "Provide details about the dispute for the record."}
                />
              )}

              <Divider sx={{ my: 2 }} />

              <RHFTextField
                name="password"
                label="Confirm Password"
                type="password"
                required
                sx={{ mb: 2 }}
                error={!!errors.password}
                helperText={errors.password?.message || "Enter your password to confirm this action."}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    required
                    name="confirm"
                    checked={values.confirm}
                    onChange={e => methods.setValue('confirm', e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    I confirm that the above information is accurate and this change is necessary.
                  </Typography>
                }
                sx={{ mb: 2 }}
              />
              {errors.confirm && (
                <Typography color="error" variant="caption">
                  {errors.confirm.message}
                </Typography>
              )}
            </Box>

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={handleClose} size="large">
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                size="large"
                variant="contained"
                loading={isSubmitting}
              >
                Submit Dispute
              </LoadingButton>
            </Stack>
          </Card>
        </FormProvider>
      </Box>
    </Modal>
  );
}
